"use client";

import type {
  CSSProperties,
} from "react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type {
  SiteBuilderDesign,
} from "@webdo/site-templates/builder";
import {
  builderBackgroundGradientOptions,
  builderBackgroundThemeOptions,
  type BuilderBackgroundGradientOption,
  type BuilderBackgroundPickerLabels,
  type BuilderBackgroundPickerValue,
  type BuilderBackgroundThemeOption,
} from "./builderBackground";

export type {
  BuilderBackgroundPickerLabels,
  BuilderBackgroundPickerValue,
  BuilderBackgroundThemeOption,
} from "./builderBackground";
export {
  builderBackgroundGradientOptions,
  builderBackgroundThemeOptions,
} from "./builderBackground";

type BuilderBackgroundPickerPosition = {
  bottom?: number;
  left: number;
  maxHeight: number;
  top?: number;
  width: number;
};

function cssBackgroundImage(value: string): string | undefined {
  const trimmed = value.trim();

  if (trimmed === "") {
    return undefined;
  }

  return /(?:gradient|image-set)\(/iu.test(trimmed)
    || /^url\(/iu.test(trimmed)
      ? trimmed
      : `url("${trimmed.replace(/"/gu, "%22")}")`;
}

function colorPickerValue(value: string, fallback: string): string {
  return /^#[0-9a-f]{6}$/iu.test(value) ? value : fallback;
}

function BackgroundPreviewCopy({
  background,
  buttonBackground,
  buttonText,
  labels,
  text,
}: {
  background: CSSProperties;
  buttonBackground: string;
  buttonText: string;
  labels: BuilderBackgroundPickerLabels;
  text: string;
}) {
  return (
    <span
      className="flex min-h-10 min-w-0 items-center gap-1.5 overflow-hidden px-2 py-1.5"
      style={{
        ...background,
        color: text,
      }}
    >
      <span className="text-sm font-bold leading-none">
        {labels.previewTitle}
      </span>
      <span className="text-[11px] leading-none">
        {labels.previewText}
      </span>
      <span
        className="rounded px-1.5 py-1 text-[11px] font-bold leading-none"
        style={{
          backgroundColor: buttonBackground,
          color: buttonText,
        }}
      >
        {labels.previewButton}
      </span>
      <span
        className="rounded border border-current px-1.5 py-1 text-[11px] font-bold leading-none"
      >
        {labels.previewButton}
      </span>
    </span>
  );
}

function ThemePreviewRow({
  checked,
  labels,
  onSelect,
  option,
}: {
  checked: boolean;
  labels: BuilderBackgroundPickerLabels;
  onSelect: () => void;
  option: BuilderBackgroundThemeOption;
}) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      className="grid w-full grid-cols-[16px_minmax(0,1fr)_22px] items-center gap-1.5 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-primary"
      data-builder-background-theme-option={option.id}
      onClick={onSelect}
    >
      <span
        className={checked
          ? "text-sm font-black leading-none text-success"
          : "text-sm font-black leading-none text-transparent"}
      >
        ✓
      </span>
      <BackgroundPreviewCopy
        background={{ backgroundColor: option.backgroundColor }}
        buttonBackground={option.buttonBackgroundColor}
        buttonText={option.buttonTextColor}
        labels={{
          ...labels,
          previewTitle: labels.themeOptions[option.id],
        }}
        text={option.textColor}
      />
      <span
        aria-hidden="true"
        className="flex h-[22px] w-[22px] items-center justify-center text-lime-300"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M14.7 2.3a1 1 0 0 1 1.4 0l1.6 1.6a1 1 0 0 1 0 1.4l-1.1 1.1-3-3 1.1-1.1ZM12.9 4.1l3 3-8.8 8.8-3.4.4.4-3.4 8.8-8.8Z" />
        </svg>
      </span>
    </button>
  );
}

function GradientPreviewRow({
  checked,
  labels,
  onSelect,
  option,
}: {
  checked: boolean;
  labels: BuilderBackgroundPickerLabels;
  onSelect: () => void;
  option: BuilderBackgroundGradientOption;
}) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      className="grid w-full grid-cols-[16px_minmax(0,1fr)_22px] items-center gap-1.5 rounded-sm text-left outline-none focus-visible:ring-2 focus-visible:ring-primary"
      data-builder-background-gradient-option={option.id}
      onClick={onSelect}
    >
      <span
        className={checked
          ? "text-sm font-black leading-none text-success"
          : "text-sm font-black leading-none text-transparent"}
      >
        ✓
      </span>
      <BackgroundPreviewCopy
        background={{ backgroundImage: option.previewValue }}
        buttonBackground={option.buttonBackgroundColor}
        buttonText={option.buttonTextColor}
        labels={{
          ...labels,
          previewTitle: labels.gradientOptions[option.id],
        }}
        text={option.previewTextColor}
      />
      <span
        aria-hidden="true"
        className="flex h-[22px] w-[22px] items-center justify-center text-lime-300"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M14.7 2.3a1 1 0 0 1 1.4 0l1.6 1.6a1 1 0 0 1 0 1.4l-1.1 1.1-3-3 1.1-1.1ZM12.9 4.1l3 3-8.8 8.8-3.4.4.4-3.4 8.8-8.8Z" />
        </svg>
      </span>
    </button>
  );
}

export function BuilderBackgroundPicker({
  design,
  label,
  labels,
  onCommit,
  onPreview,
  value,
}: {
  design: SiteBuilderDesign;
  label: string;
  labels: BuilderBackgroundPickerLabels;
  onCommit: (value: BuilderBackgroundPickerValue) => void;
  onPreview: (value: BuilderBackgroundPickerValue) => void;
  value: BuilderBackgroundPickerValue;
}) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const customChangedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] =
    useState<BuilderBackgroundPickerPosition | null>(null);
  const [tab, setTab] = useState<"custom" | "gradient" | "theme">("theme");
  const themeOptions = useMemo(
    () => builderBackgroundThemeOptions(design),
    [design],
  );
  const gradientOptions = useMemo(
    () => builderBackgroundGradientOptions(design),
    [design],
  );
  const fallbackColor = themeOptions[0]?.backgroundColor ?? "#ffffff";
  const selectedThemeOption = value.backgroundImage === ""
    ? themeOptions.find((option) => (
        option.backgroundValue === value.backgroundColor
      )) ?? null
    : null;
  const selectedGradientOption = value.backgroundImage
    ? gradientOptions.find((option) => option.value === value.backgroundImage)
      ?? null
    : null;
  const pickerValue = colorPickerValue(
    value.backgroundColor,
    colorPickerValue(fallbackColor, "#ffffff"),
  );
  const swatchStyle: CSSProperties = selectedGradientOption
    ? { backgroundImage: selectedGradientOption.previewValue }
    : value.backgroundImage
      ? { backgroundImage: cssBackgroundImage(value.backgroundImage) }
      : {
          backgroundColor: (
            selectedThemeOption?.backgroundColor ?? value.backgroundColor
          ) || fallbackColor,
        };
  const selectedLabel = selectedGradientOption
    ? labels.gradientOptions[selectedGradientOption.id]
    : selectedThemeOption
      ? labels.themeOptions[selectedThemeOption.id]
      : value.backgroundImage || value.backgroundColor
        ? value.backgroundImage ? labels.gradient : labels.custom
        : labels.default;
  const selectValue = (nextValue: BuilderBackgroundPickerValue) => {
    customChangedRef.current = false;
    onCommit(nextValue);
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setPopoverPosition(null);

      return;
    }

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      const viewportPadding = 8;
      const width = Math.min(
        328,
        window.innerWidth - (viewportPadding * 2),
      );
      const left = Math.min(
        Math.max(viewportPadding, rect.right - width),
        window.innerWidth - width - viewportPadding,
      );
      const belowHeight = window.innerHeight - rect.bottom - viewportPadding;
      const aboveHeight = rect.top - viewportPadding;
      const openAbove = belowHeight < 280 && aboveHeight > belowHeight;
      const maxHeight = Math.max(
        160,
        openAbove ? aboveHeight : belowHeight,
      );

      setPopoverPosition({
        bottom: openAbove
          ? window.innerHeight - rect.top + viewportPadding
          : undefined,
        left,
        maxHeight,
        top: openAbove
          ? undefined
          : rect.bottom + viewportPadding,
        width,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOnOutsidePointer = (event: globalThis.PointerEvent) => {
      const target = event.target instanceof Node ? event.target : null;

      if (
        !target
        || buttonRef.current?.contains(target)
        || popoverRef.current?.contains(target)
      ) {
        return;
      }

      setOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const popover = open
    && popoverPosition
    && typeof document !== "undefined"
      ? createPortal(
        <div
          ref={popoverRef}
          aria-label={label}
          className="fixed z-[260] overflow-auto rounded-md border border-border bg-surface p-1.5 text-xs text-text shadow-2xl"
          data-builder-background-picker-popover="true"
          role="dialog"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          style={{
            bottom: popoverPosition.bottom,
            left: popoverPosition.left,
            maxHeight: popoverPosition.maxHeight,
            top: popoverPosition.top,
            width: popoverPosition.width,
          }}
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex min-w-0 gap-1">
              {(["theme", "custom", "gradient"] as const).map((item) => (
                <button
                  type="button"
                  aria-pressed={item === tab}
                  className={item === tab
                    ? "rounded border border-primary px-2 py-1 text-xs font-bold text-text"
                    : "rounded px-2 py-1 text-xs font-bold text-text-secondary hover:bg-surface"}
                  data-builder-background-picker-tab={item}
                  key={item}
                  onClick={() => setTab(item)}
                >
                  {labels[item]}
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-label={labels.default}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-surface text-text hover:bg-surface"
              data-builder-background-picker-reset="true"
              onClick={() => selectValue({
                backgroundColor: "",
                backgroundImage: "",
                textColor: "",
              })}
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 2a1 1 0 0 0-.9.55L6.38 4H3a1 1 0 0 0 0 2h.35l.86 10.34A2 2 0 0 0 6.2 18h7.6a2 2 0 0 0 1.99-1.66L16.65 6H17a1 1 0 1 0 0-2h-3.38l-.72-1.45A1 1 0 0 0 12 2H8Zm1 7a1 1 0 0 0-2 0v5a1 1 0 1 0 2 0V9Zm4 0a1 1 0 1 0-2 0v5a1 1 0 1 0 2 0V9Z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {tab === "theme" ? (
            <div className="space-y-1">
              {themeOptions.map((option) => (
                <ThemePreviewRow
                  checked={
                    value.backgroundImage === ""
                    && value.backgroundColor === option.backgroundValue
                  }
                  key={option.id}
                  labels={labels}
                  onSelect={() => selectValue({
                    backgroundColor: option.backgroundValue,
                    backgroundImage: "",
                    textColor: option.textValue,
                  })}
                  option={option}
                />
              ))}
            </div>
          ) : null}

          {tab === "custom" ? (
            <div className="space-y-3 p-2">
              <label className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 text-xs">
                <span className="text-text">
                  {labels.customColor}
                </span>
                <span className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label={labels.customColor}
                    className="h-8 w-10 shrink-0 cursor-pointer rounded border border-border bg-transparent p-1"
                    value={pickerValue}
                    onBlur={(event) => {
                      if (!customChangedRef.current) {
                        return;
                      }

                      customChangedRef.current = false;
                      onCommit({
                        backgroundColor: event.currentTarget.value,
                        backgroundImage: "",
                        textColor: value.textColor,
                      });
                    }}
                    onChange={(event) => {
                      customChangedRef.current = true;
                      onPreview({
                        backgroundColor: event.currentTarget.value,
                        backgroundImage: "",
                        textColor: value.textColor,
                      });
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className="h-8 flex-1 rounded border border-border"
                    style={{ backgroundColor: pickerValue }}
                  />
                </span>
              </label>
            </div>
          ) : null}

          {tab === "gradient" ? (
            <div className="space-y-1">
              {gradientOptions.map((option) => (
                <GradientPreviewRow
                  checked={value.backgroundImage === option.value}
                  key={option.id}
                  labels={labels}
                  onSelect={() => selectValue({
                    backgroundColor: "",
                    backgroundImage: option.value,
                    textColor: option.textColor,
                  })}
                  option={option}
                />
              ))}
            </div>
          ) : null}
        </div>,
        document.body,
      )
      : null;

  return (
    <div
      className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-2 py-1.5 text-xs"
      data-builder-background-picker="true"
    >
      <span className="min-w-0 text-text">
        {label}
      </span>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-label={label}
        className="flex h-8 min-w-0 items-center gap-2 rounded border border-border bg-surface px-2 text-left text-xs text-text outline-none focus:border-primary"
        onClick={() => setOpen((current) => !current)}
      >
        <span
          aria-hidden="true"
          className="h-5 w-8 shrink-0 rounded border border-border-strong"
          style={swatchStyle}
        />
        <span className="min-w-0 truncate">
          {selectedLabel}
        </span>
      </button>
      {popover}
    </div>
  );
}
